import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone } from 'lucide-react';
import { FOOTER_LINKS } from '@/constants/navigation';
import { ROUTES } from '@/config/routes';
import { usePolicyLinks } from '@/hooks/usePolicyLinks';
import { useWebsiteSettings } from '@/hooks/useWebsiteSettings';

const DEFAULT_STORE_NAME = 'Beauty Sports';
const DEFAULT_DESCRIPTION = 'Your one-stop destination for premium sports equipment and gear. Quality products for every sport.';
const DEFAULT_PHONE = '+91 1800-123-4567';
const DEFAULT_EMAIL = 'support@beautysports.com';
const DEFAULT_ADDRESS = 'Opposite Jubly Restaurant, Vinayaka Hospital Road, Sulthan Bathery, Wayanad-673592, Kerala';

function cleanPhone(value = '') {
  return value.replace(/[^0-9]/g, '');
}

function externalUrl(value = '') {
  const url = value.trim();
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url.replace(/^\/+/, '')}`;
}

function formatAddress(settings: ReturnType<typeof useWebsiteSettings>) {
  const store = settings?.store || {};
  const parts = [store.address, store.city, store.postalCode, store.state].filter(Boolean);
  return parts.length ? parts.join(', ') : DEFAULT_ADDRESS;
}

export function Footer() {
  const policyLinks = usePolicyLinks();
  const settings = useWebsiteSettings();
  const companyLinks = [...FOOTER_LINKS.company, ...policyLinks.links];

  const storeName = settings?.general?.storeName || DEFAULT_STORE_NAME;
  const description = settings?.website?.footerDescription || DEFAULT_DESCRIPTION;
  const phone = settings?.website?.footerPhone || DEFAULT_PHONE;
  const email = settings?.website?.footerEmail || DEFAULT_EMAIL;
  const address = formatAddress(settings);
  const whatsappNumber = cleanPhone(settings?.website?.whatsappNumber || phone);

  const socialLinks = [
    whatsappNumber ? {
      label: 'WhatsApp',
      href: `https://wa.me/${whatsappNumber}`,
      icon: <WhatsAppIcon className="h-4 w-4" />,
    } : null,
    settings?.website?.instagramUrl ? {
      label: 'Instagram',
      href: externalUrl(settings.website.instagramUrl),
      icon: <InstagramIcon className="h-4 w-4" />,
    } : null,
    settings?.website?.facebookUrl ? {
      label: 'Facebook',
      href: externalUrl(settings.website.facebookUrl),
      icon: <FacebookIcon className="h-4 w-4" />,
    } : null,
    settings?.website?.youtubeUrl ? {
      label: 'YouTube',
      href: externalUrl(settings.website.youtubeUrl),
      icon: <YoutubeIcon className="h-4 w-4" />,
    } : null,
  ].filter(Boolean) as Array<{ label: string; href: string; icon: React.ReactNode }>;

  return (
    <footer className="border-t border-black/10 bg-[#C9C6C5]">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)]">
          <div>
            <Link to={ROUTES.HOME} className="text-2xl font-bold tracking-tight text-text-primary uppercase">
              {storeName}
            </Link>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-text-secondary">
              {description}
            </p>

            {socialLinks.length > 0 && (
              <div className="mt-5 flex items-center gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={social.label}
                    className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-full)] border border-black/10 bg-white/40 text-text-secondary transition-colors hover:border-text-primary hover:text-text-primary"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-[minmax(160px,0.7fr)_minmax(0,1.3fr)]">
            <FooterLinkGroup title="COMPANY" links={companyLinks} />

            <div>
              <h4 className="mb-4 text-sm font-semibold text-text-primary">CONTACT</h4>
              <div className="space-y-3 text-text-secondary">
                {phone && <ContactItem icon={<Phone className="h-4 w-4" />} text={phone} />}
                {email && <ContactItem icon={<Mail className="h-4 w-4" />} text={email} />}
                {address && <ContactItem icon={<MapPin className="h-4 w-4" />} text={address} />}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-black/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-4 text-xs text-text-secondary sm:flex-row sm:px-6 lg:px-8">
          <p>© {new Date().getFullYear()} {storeName}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to={policyLinks.privacy.href} className="hover:text-text-primary transition-colors">{policyLinks.privacy.label}</Link>
            <Link to={policyLinks.terms.href} className="hover:text-text-primary transition-colors">{policyLinks.terms.label}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLinkGroup({ title, links }: { title: string; links: Array<{ label: string; href: string }> }) {
  return (
    <div>
      <h4 className="mb-4 text-sm font-semibold text-text-primary">{title}</h4>
      <ul className="space-y-2.5">
        {links.map((link) => (
          <li key={link.label}>
            <Link to={link.href} className="text-sm text-text-secondary transition-colors hover:text-text-primary">
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContactItem({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-start gap-2 text-sm text-text-secondary">
      <span className="mt-0.5 shrink-0 text-text-secondary">{icon}</span>
      <span>{text}</span>
    </div>
  );
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.86 9.86 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91a9.86 9.86 0 0 0-2.91-7.01Zm-7 15.25h-.01a8.2 8.2 0 0 1-4.17-1.14l-.3-.18-3.11.82.83-3.04-.2-.31a8.25 8.25 0 0 1-1.26-4.4c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.82c-.02 4.55-3.72 8.25-8.26 8.25Zm4.52-6.17c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.79.97-.15.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.24-.74-.66-1.24-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.76-1.85-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.08.15-1.18-.06-.11-.22-.17-.47-.29Z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect width="17" height="17" x="3.5" y="3.5" rx="5" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="3.6" stroke="currentColor" strokeWidth="2" />
      <circle cx="17" cy="7" r="1.2" fill="currentColor" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M14 8.5V6.75c0-.5.34-.75.85-.75H17V2.2C16.63 2.15 15.35 2 13.85 2 10.72 2 8.6 3.9 8.6 7.38V8.5H5v4.25h3.6V22H13v-9.25h3.4L17 8.5h-3Z" />
    </svg>
  );
}

function YoutubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M21.58 7.2a2.75 2.75 0 0 0-1.94-1.95C17.93 4.8 12 4.8 12 4.8s-5.93 0-7.64.45A2.75 2.75 0 0 0 2.42 7.2 28.62 28.62 0 0 0 2 12a28.62 28.62 0 0 0 .42 4.8 2.75 2.75 0 0 0 1.94 1.95c1.71.45 7.64.45 7.64.45s5.93 0 7.64-.45a2.75 2.75 0 0 0 1.94-1.95A28.62 28.62 0 0 0 22 12a28.62 28.62 0 0 0-.42-4.8ZM10 15.2V8.8l5.2 3.2L10 15.2Z" />
    </svg>
  );
}




